import config from "@/config";
import { authPatch } from "@/store/authStoreRequest";
import { getAccessToken } from "@/utils/accessToken";

import { getPersistedAgenticCorrelation } from "./agenticAnalyticsCorrelation";
import { getStoredAttribution, isSynced, markSynced } from "./attribution";
import log from "./log";

/**
 * Moves parked attribution from localStorage onto the user row, mirroring
 * userConsentPersistence.ts: nothing can be written until an access token
 * exists, so data captured before login waits in the browser until there is an
 * identity to attach it to.
 *
 * Deliberately fire-and-forget. Attribution is marketing data and must never
 * block or break a login. The local copy is kept (not cleared) after a
 * successful write, so a silently failed write can still be retried.
 */
export async function syncStoredAttribution(): Promise<void> {
  if (!getAccessToken()) {
    return;
  }

  const attribution = getStoredAttribution();
  const shouldSyncAttribution = Boolean(attribution && !isSynced(attribution));
  const { anonymousId } = getPersistedAgenticCorrelation();
  if (!shouldSyncAttribution && !anonymousId) {
    return;
  }

  try {
    await authPatch(
      `${config.backendURL}/user`,
      {
        ...(shouldSyncAttribution ? { attribution } : {}),
        ...(anonymousId ? { anonymous_id: anonymousId } : {}),
      },
      {}
    );
    if (shouldSyncAttribution && attribution) markSynced(attribution);
  } catch (error) {
    log.error("failed to persist user attribution", error);
  }
}
